import { BaseAdapter } from './BaseAdapter';
import { 
  WalletInfo, 
  ConnectOptions, 
  TransactionRequest, 
  WalletType 
} from '../core/types';
import { 
  WalletNotFoundError, 
  UserRejectedError, 
  TransactionError,
  ChainNotSupportedError 
} from '../core/errors';
import { getChainById } from '../core/chains';

declare global {
  interface Window {
    ethereum?: any;
    BinanceChain?: any;
  }
}

export class WebAdapter extends BaseAdapter {
  constructor() {
    super();
    this.detectAndSetupProvider();
  }

  private detectAndSetupProvider(): void {
    if (typeof window === 'undefined') return;

    // Check for Binance Wallet first
    if (window.BinanceChain) {
      this._provider = window.BinanceChain;
      this._walletType = WalletType.BINANCE;
    }
    // Then check for MetaMask or other Ethereum providers
    else if (window.ethereum) {
      this._provider = window.ethereum;
      
      // Detect specific wallet types
      if (window.ethereum.isMetaMask) {
        this._walletType = WalletType.METAMASK;
      } else if (window.ethereum.isTrust) {
        this._walletType = WalletType.TRUST;
      }
    }

    if (this._provider) {
      this.setupEventListeners();
    }
  }

  private setupEventListeners(): void {
    if (!this._provider) return;

    this._provider.on('accountsChanged', (accounts: string[]) => {
      this.handleAccountsChanged(accounts);
    });

    this._provider.on('chainChanged', (chainId: string) => {
      this.handleChainChanged(chainId);
    });

    this._provider.on('disconnect', () => {
      this.handleDisconnect();
    });
  }

  async connect(options?: ConnectOptions): Promise<WalletInfo> {
    if (!this._provider) {
      throw new WalletNotFoundError('No Web3 wallet found');
    }

    try {
      // Request account access
      const accounts = await this._provider.request({ 
        method: 'eth_requestAccounts' 
      });

      if (accounts.length === 0) {
        throw new UserRejectedError();
      }

      this._address = accounts[0];
      
      // Get current chain ID
      const chainId = await this._provider.request({ 
        method: 'eth_chainId' 
      });
      this._chainId = chainId;

      // Switch to BNB Chain if requested and not already on it
      if (options?.chainId && chainId !== options.chainId) {
        await this.switchChain(options.chainId);
      }

      this._isConnected = true;

      return {
        address: this._address!,
        chainId: this._chainId!,
        provider: this._provider,
        isConnected: true,
        walletType: this._walletType,
      };
    } catch (error: any) {
      if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
        throw new UserRejectedError();
      }
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    this.handleDisconnect();
  }

  async switchChain(chainId: string): Promise<void> {
    if (!this._provider) {
      throw new WalletNotFoundError('No wallet connected');
    }

    try {
      await this._provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
    } catch (error: any) {
      // Chain not added to wallet, try to add it
      if (error.code === 4902) {
        const chainConfig = getChainById(chainId);
        if (!chainConfig) {
          throw new ChainNotSupportedError(chainId);
        }

        try {
          await this._provider.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: chainConfig.chainId,
              chainName: chainConfig.chainName,
              nativeCurrency: chainConfig.nativeCurrency,
              rpcUrls: chainConfig.rpcUrls,
              blockExplorerUrls: chainConfig.blockExplorerUrls,
            }],
          });
        } catch (addError) {
          throw new ChainNotSupportedError(chainId);
        }
      } else if (error.code === 4001) {
        throw new UserRejectedError('User rejected chain switch');
      } else {
        throw error;
      }
    }
  }

  async sendTransaction(tx: TransactionRequest): Promise<string> {
    if (!this._provider || !this._isConnected) {
      throw new TransactionError('Wallet not connected');
    }

    try {
      const txHash = await this._provider.request({
        method: 'eth_sendTransaction',
        params: [{
          from: this._address,
          to: tx.to,
          value: tx.value,
          data: tx.data,
          gas: tx.gasLimit,
          gasPrice: tx.gasPrice,
        }],
      });

      return txHash;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new UserRejectedError('User rejected transaction');
      }
      throw new TransactionError(error.message || 'Transaction failed');
    }
  }

  async signMessage(message: string): Promise<string> {
    if (!this._provider || !this._isConnected) {
      throw new WalletNotFoundError('Wallet not connected');
    }

    try {
      const signature = await this._provider.request({
        method: 'personal_sign',
        params: [message, this._address],
      });

      return signature;
    } catch (error: any) {
      if (error.code === 4001) {
        throw new UserRejectedError('User rejected signature request');
      }
      throw error;
    }
  }
}